import { Card, CardContent, CardHeader, CardTitle } from "@devhop/ui";
import { CalendarIcon, ClockIcon, TrendingDownIcon } from "lucide-react";
import type { AttendanceSummaryDTO } from "@/modules/students/domain/repo/get-attendance-summary";
import { formatDate } from "@/shared/lib/date-time";

type AttendanceSummaryCardsProps = {
  summary: AttendanceSummaryDTO | undefined;
  isLoading?: boolean;
};

export function AttendanceSummaryCards({
  summary,
  isLoading,
}: AttendanceSummaryCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardHeader className="pb-3">
              <CardTitle className="font-normal text-muted-foreground text-sm">
                ...
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="font-semibold text-lg">...</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {/* Total Attendances Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-normal text-muted-foreground text-sm">
              ຈຳນວນການເຂົ້າຮຽນທັງໝົດ
            </CardTitle>
            <CalendarIcon className="size-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <p className="font-semibold text-lg">
            {summary?.totalAttendances ?? 0}
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            {summary && summary.totalAttendances > 0
              ? "ມີການເຂົ້າຮຽນແລ້ວ"
              : "ຍັງບໍ່ມີການເຂົ້າຮຽນ"}
          </p>
        </CardContent>
      </Card>

      {/* Absence Rate Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-normal text-muted-foreground text-sm">
              ອັດຕາການຂາດຮຽນ
            </CardTitle>
            <TrendingDownIcon className="size-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          <p className="font-semibold text-lg">
            {summary?.absenceRate.toFixed(1) ?? "0.0"}%
          </p>
          <p className="mt-1 text-muted-foreground text-xs">
            ຄຳນວນຈາກການເຂົ້າຮຽນທັງໝົດ
          </p>
        </CardContent>
      </Card>

      {/* Last Activity Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-normal text-muted-foreground text-sm">
              ການເຂົ້າຮຽນຄັ້ງລ່າສຸດ
            </CardTitle>
            <ClockIcon className="size-4 text-muted-foreground" />
          </div>
        </CardHeader>
        <CardContent>
          {summary?.lastActivity ? (
            <>
              <p className="font-semibold text-lg">
                {formatDate(summary.lastActivity.checkedInAt)}
              </p>
              <p className="mt-1 text-muted-foreground text-xs">
                {summary.lastActivity.checkedInTime} check-in
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-lg">-</p>
              <p className="mt-1 text-muted-foreground text-xs">
                ຍັງບໍ່ມີການເຂົ້າຮຽນ
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
